import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  ClipboardList,
  Search,
  Plus,
  Clock,
  CheckCircle2,
  AlertCircle,
  Eye,
  Loader2,
  Calendar,
  Truck,
} from "lucide-react";
import { maintenanceService } from "../../services/maintenance/planMaestroService";
import { CloseOTModal } from "./CloseOTModal";
import DetalleOrden from "./DetalleOrden";

interface OrdenTrabajo {
  id: number;
  codigo_ot: string;
  vehiculo_id: string;
  tipo: string;
  prioridad: string;
  estado: string;
  fecha_programada: string;
  kilometraje_inicio: number;
  costo_total: number;
  vehiculo: {
    placa: string;
    marca: string;
    modelo: string;
  };
}

export default function GestionOrdenes() {
  const [ots, setOts] = useState<OrdenTrabajo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEstado, setFilterEstado] = useState("TODAS");
  const [selectedOtForClose, setSelectedOtForClose] =
    useState<OrdenTrabajo | null>(null);
  const [viewingOtId, setViewingOtId] = useState<number | null>(null);
  const [filterPrioridad, setFilterPrioridad] = useState("TODAS");
  const [filterTipo, setFilterTipo] = useState("TODOS");
  const [filterFechaInicio, setFilterFechaInicio] = useState("");
  const [filterFechaFin, setFilterFechaFin] = useState("");

  const fetchOTs = async (status?: string, search?: string, prioridad?: string, tipo?: string) => {
    try {
      setLoading(true);
      const response = await maintenanceService.getOrdenesTrabajo({
        status: status || filterEstado,
        search: search || searchTerm,
        prioridad: prioridad || filterPrioridad,
        tipo: tipo || filterTipo,
        fecha_inicio: filterFechaInicio || undefined,
        fecha_fin: filterFechaFin || undefined
      });
      setOts(response.data);
    } catch (error) {
      console.error("Error fetching OTs:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    // Avoid "Day Before" bug by ensuring date is treated as local time (ignoring time if present)
    const datePart = dateStr.split('T')[0];
    const date = new Date(`${datePart}T12:00:00`);
    return new Intl.DateTimeFormat("es-PE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  };

  useEffect(() => {
    fetchOTs();
  }, []);

  const getStatusInfo = (estado: string) => {
    switch (estado) {
      case "ABIERTA":
        return {
          color: "bg-blue-100 text-blue-700 border-blue-200",
          icon: Clock,
          label: "Abierta",
        };
      case "EN_PROGRESO":
        return {
          color: "bg-yellow-100 text-yellow-700 border-yellow-200",
          icon: AlertCircle,
          label: "En Progreso",
        };
      case "CERRADA":
        return {
          color: "bg-green-100 text-green-700 border-green-200",
          icon: CheckCircle2,
          label: "Cerrada",
        };
      case "CANCELADA":
        return {
          color: "bg-red-100 text-red-700 border-red-200",
          icon: AlertCircle,
          label: "Cancelada",
        };
      default:
        return {
          color: "bg-gray-100 text-gray-700 border-gray-200",
          icon: Clock,
          label: estado,
        };
    }
  };

  const getPrioridadColor = (prioridad: string) => {
    switch (prioridad) {
      case "CRITICA":
        return "text-red-600 font-black";
      case "ALTA":
        return "text-orange-600 font-bold";
      case "MEDIA":
        return "text-blue-600 font-semibold";
      default:
        return "text-gray-600";
    }
  };

  // ⚡️ OPTIMIZACIÓN: Debounce para búsqueda y filtros en servidor
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchOTs();
    }, 400); // 400ms de calma antes de ir al servidor

    return () => clearTimeout(timer);
  }, [searchTerm, filterEstado, filterPrioridad, filterTipo, filterFechaInicio, filterFechaFin]);

  const handleUpdateOtLocal = (data: { id: number; estado: string }) => {
    setOts(prev => prev.map(ot => ot.id === data.id ? { ...ot, estado: data.estado } : ot));
  };

  return (
    <div className="space-y-4">
      <div className={viewingOtId ? "hidden" : "block"}>
        <Card className="shadow-sm border-none bg-transparent">
          <CardHeader className="p-0 pb-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <CardTitle className="text-2xl font-bold text-gray-800 tracking-tight">
                  Órdenes de Trabajo
                </CardTitle>
                <p className="text-sm text-gray-500 font-medium mt-1">
                  Control y ejecución de mantenimientos vehiculares
                </p>
              </div>
              <Button className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all font-bold">
                <Plus className="h-4 w-4 mr-2" />
                Nueva OT Manual
              </Button>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="p-4 border-b border-slate-50 bg-slate-50/30 flex flex-col md:flex-row gap-4 items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por OT o placa..."
                    className="pl-10 h-10 bg-white border-slate-200 focus:ring-2 focus:ring-blue-100 transition-all"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="flex flex-wrap gap-2 w-full md:w-auto">
                  <div className="flex items-center gap-2 bg-white px-2 py-0.5 rounded-md border border-slate-200">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    <input
                      type="date"
                      className="h-8 text-xs font-medium focus:outline-none bg-transparent"
                      value={filterFechaInicio}
                      onChange={(e) => setFilterFechaInicio(e.target.value)}
                      title="Fecha Desde"
                    />
                    <span className="text-slate-300">|</span>
                    <input
                      type="date"
                      className="h-8 text-xs font-medium focus:outline-none bg-transparent"
                      value={filterFechaFin}
                      onChange={(e) => setFilterFechaFin(e.target.value)}
                      title="Fecha Hasta"
                    />
                    {(filterFechaInicio || filterFechaFin) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-slate-400 hover:text-red-500 hover:bg-red-50"
                        onClick={() => {
                          setFilterFechaInicio("");
                          setFilterFechaFin("");
                        }}
                      >
                        <Plus className="h-3 w-3 rotate-45" />
                      </Button>
                    )}
                  </div>

                  <select
                    className="h-10 px-3 rounded-md border border-slate-200 text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
                    value={filterTipo}
                    onChange={(e) => setFilterTipo(e.target.value)}
                  >
                    <option value="TODOS">Todos los tipos</option>
                    <option value="PREVENTIVO">Preventivo</option>
                    <option value="CORRECTIVO">Correctivo</option>
                  </select>

                  <select
                    className="h-10 px-3 rounded-md border border-slate-200 text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
                    value={filterPrioridad}
                    onChange={(e) => setFilterPrioridad(e.target.value)}
                  >
                    <option value="TODAS">Todas las prioridades</option>
                    <option value="CRITICA">Crítica</option>
                    <option value="ALTA">Alta</option>
                    <option value="MEDIA">Media</option>
                    <option value="BAJA">Baja</option>
                  </select>

                  <select
                    className="h-10 px-3 rounded-md border border-slate-200 text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
                    value={filterEstado}
                    onChange={(e) => setFilterEstado(e.target.value)}
                  >
                    <option value="TODAS">Todos los estados</option>
                    <option value="ABIERTA">Abiertas</option>
                    <option value="EN_PROGRESO">En Progreso</option>
                    <option value="CERRADA">Cerradas</option>
                    <option value="CANCELADA">Canceladas</option>
                  </select>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 shrink-0"
                    onClick={() => fetchOTs()}
                  >
                    <Clock
                      className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                    />
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto">
                {loading && ots.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <Loader2 className="h-10 w-10 animate-spin text-blue-600 mb-4" />
                    <p className="text-slate-500 font-medium">
                      Cargando órdenes...
                    </p>
                  </div>
                ) : ots.length === 0 ? (
                  <div className="text-center py-20">
                    <ClipboardList className="h-16 w-16 text-slate-200 mx-auto mb-4" />
                    <p className="text-slate-500 font-bold text-lg">
                      No hay órdenes encontradas
                    </p>
                    <p className="text-sm text-slate-400">
                      Las OTs creadas desde la flota aparecerán aquí.
                    </p>
                  </div>
                ) : (
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-slate-50/50">
                      <tr>
                        <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">
                          Código OT
                        </th>
                        <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">
                          Vehículo
                        </th>
                        <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">
                          Tipo / Prioridad
                        </th>
                        <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">
                          Programado
                        </th>
                        <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">
                          Estado
                        </th>
                        <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {ots.map((ot) => {
                        const status = getStatusInfo(ot.estado);
                        return (
                          <tr
                            key={ot.id}
                            className="hover:bg-blue-50/30 transition-colors group"
                          >
                            <td className="px-6 py-4 max-w-[150px]">
                              <div className="flex flex-col">
                                <span className="font-black text-slate-900 group-hover:text-blue-600 transition-colors">
                                  {ot.codigo_ot}
                                </span>
                                <span className="text-[10px] text-slate-400 font-medium uppercase mt-0.5">
                                  ID: {ot.id}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">
                                  <Truck className="h-5 w-5" />
                                </div>
                                <div className="flex flex-col">
                                  <span className="font-bold text-slate-800 text-sm">
                                    {ot.vehiculo?.placa}
                                  </span>
                                  <span className="text-xs text-slate-500 truncate max-w-[120px]">
                                    {ot.vehiculo?.marca} {ot.vehiculo?.modelo}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-col gap-1">
                                <Badge
                                  variant="outline"
                                  className="w-fit text-[10px] font-bold py-0 h-5 px-1.5 border-slate-200"
                                >
                                  {ot.tipo}
                                </Badge>
                                <span
                                  className={`text-[11px] uppercase tracking-tighter ${getPrioridadColor(ot.prioridad)}`}
                                >
                                  {ot.prioridad}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2 text-slate-600">
                                <Calendar className="h-3.5 w-3.5 text-slate-400" />
                                <span className="text-sm font-medium">
                                  {formatDate(ot.fecha_programada)}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <Badge
                                className={`${status.color} shadow-none py-1 h-7 border`}
                              >
                                <status.icon className="h-3.5 w-3.5 mr-1.5" />
                                {status.label}
                              </Badge>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                {ot.estado === "ABIERTA" && (
                                  <Button
                                    size="sm"
                                    className="h-8 px-3 bg-green-600 hover:bg-green-700 text-white text-xs gap-1.5"
                                    onClick={() => setSelectedOtForClose(ot)}
                                  >
                                    Cerrar OT
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 px-3 gap-1.5 text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                                  onClick={() => setViewingOtId(ot.id)}
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                  <span className="text-xs font-medium">Ver</span>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {viewingOtId && (
        <DetalleOrden
          otId={viewingOtId}
          onBack={() => setViewingOtId(null)}
          onUpdate={handleUpdateOtLocal}
        />
      )}

      {selectedOtForClose && (
        <CloseOTModal
          open={!!selectedOtForClose}
          onClose={() => setSelectedOtForClose(null)}
          ot={selectedOtForClose}
          onSuccess={fetchOTs}
        />
      )}
    </div>
  );
}
